import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import AdminLayout from "./AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Search, Filter, ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

// Types for audit events
interface AuditActor {
  id: number;
  displayName: string;
  email?: string;
}

interface AuditTarget {
  id: number;
  displayName: string;
  email?: string;
}

interface AuditEventDetails {
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  reason?: string;
  summary?: string;
  [key: string]: unknown;
}

interface AuditEvent {
  id: number;
  timestamp: string;
  actor: AuditActor;
  target?: AuditTarget | null;
  category: string;
  action: string;
  summary: string;
  details?: AuditEventDetails;
}

interface PaginatedAuditEvents {
  events: AuditEvent[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface AuditCategory {
  value: string;
  label: string;
}

// Category badge colors
const categoryColors: Record<string, string> = {
  rbac: "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  "mcp-tools": "bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  orchestration: "bg-amber-100 dark:bg-amber-900/50 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  skills: "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 border-green-200 dark:border-green-800",
  "admin-settings": "bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  impersonation: "bg-rose-100 dark:bg-rose-900/50 text-rose-800 dark:text-rose-300 border-rose-200 dark:border-rose-800",
  unclaimed: "bg-teal-100 dark:bg-teal-900/50 text-teal-800 dark:text-teal-300 border-teal-200 dark:border-teal-800",
};

// Date range presets
const dateRanges = [
  { label: "Last 24 hours", value: "24h" },
  { label: "Last 7 days", value: "7d" },
  { label: "Last 30 days", value: "30d" },
  { label: "All time", value: "all" },
];

function getDateRange(preset: string): { from?: string; to?: string } {
  const now = new Date();
  switch (preset) {
    case "24h":
      return {
        from: new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString(),
        to: now.toISOString(),
      };
    case "7d":
      return {
        from: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        to: now.toISOString(),
      };
    case "30d":
      return {
        from: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        to: now.toISOString(),
      };
    default:
      return {};
  }
}

export default function AdminAuditPage() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [category, setCategory] = useState<string>("all");
  const [dateRange, setDateRange] = useState("7d");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

  // Fetch categories
  const { data: categories = [] } = useQuery<AuditCategory[]>({
    queryKey: ["/api/admin/audit/categories"],
  });

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));

    if (category && category !== "all") {
      params.set("category", category);
    }

    const range = getDateRange(dateRange);
    if (range.from) params.set("from", range.from);
    if (range.to) params.set("to", range.to);

    return params.toString();
  }, [page, pageSize, category, dateRange]);

  // Fetch audit events
  const { data, isLoading, error } = useQuery<PaginatedAuditEvents>({
    queryKey: [`/api/admin/audit/events?${queryParams}`],
  });

  // Filter events by search query (client-side for simplicity)
  const filteredEvents = useMemo(() => {
    if (!data?.events) return [];
    if (!searchQuery.trim()) return data.events;

    const query = searchQuery.toLowerCase();
    return data.events.filter(
      (event) =>
        event.actor.displayName.toLowerCase().includes(query) ||
        event.actor.email?.toLowerCase().includes(query) ||
        event.target?.displayName.toLowerCase().includes(query) ||
        event.target?.email?.toLowerCase().includes(query) ||
        event.summary.toLowerCase().includes(query) ||
        event.action.toLowerCase().includes(query)
    );
  }, [data?.events, searchQuery]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (data?.totalPages ?? 1)) {
      setPage(newPage);
    }
  };

  return (
    <AdminLayout
      title="Audit Logs"
      description="View a read-only log of administrative actions including RBAC changes, MCP tool updates, configuration changes, and impersonation sessions. Failed unclaimed-commission claim notifications are also recorded for support follow-up."
    >
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-500 dark:text-slate-400" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Filters:</span>
          </div>

          {/* Date range */}
          <Select value={dateRange} onValueChange={(v) => { setDateRange(v); setPage(1); }}>
            <SelectTrigger aria-label="Date range" className="w-[160px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {dateRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Category */}
          <Select value={category} onValueChange={(v) => { setCategory(v); setPage(1); }}>
            <SelectTrigger aria-label="Category" className="w-[180px]">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              aria-label="Search audit events by actor, target, or action"
              placeholder="Search by actor, target, or action..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Events table */}
        <div className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-orange-600 dark:text-orange-400" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-16 text-red-600 dark:text-red-400">
              Failed to load audit events
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-500 dark:text-slate-400">
              <p>No audit events found</p>
              <p className="text-sm">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-gray-900">
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead className="w-[80px]">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEvents.map((event) => (
                    <TableRow
                      key={event.id}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700"
                      onClick={() => setSelectedEvent(event)}
                    >
                      <TableCell className="font-mono text-sm text-slate-600 dark:text-slate-400">
                        {format(new Date(event.timestamp), "MMM d, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium text-slate-900 dark:text-slate-100">
                            {event.actor.displayName}
                          </div>
                          {event.actor.email && (
                            <div className="text-xs text-slate-500 dark:text-slate-400">
                              {event.actor.email}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {event.target ? (
                          <div>
                            <div className="font-medium text-slate-900 dark:text-slate-100">
                              {event.target.displayName}
                            </div>
                            {event.target.email && (
                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                {event.target.email}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            "font-medium",
                            categoryColors[event.category] ?? categoryColors["admin-settings"]
                          )}
                        >
                          {event.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-700 dark:text-slate-300">{event.summary}</span>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label={`View details: ${event.summary}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedEvent(event);
                          }}
                        >
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data && data.totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-slate-200 dark:border-gray-700 px-4 py-3">
                  <div className="text-sm text-slate-600 dark:text-slate-400">
                    Showing {(page - 1) * pageSize + 1} to{" "}
                    {Math.min(page * pageSize, data.total)} of {data.total} events
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Page {page} of {data.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page >= data.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Event detail dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Event Details</DialogTitle>
            <DialogDescription>
              {selectedEvent &&
                format(new Date(selectedEvent.timestamp), "MMMM d, yyyy 'at' HH:mm:ss")}
            </DialogDescription>
          </DialogHeader>

          {selectedEvent && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                    Category
                  </label>
                  <div className="mt-1">
                    <Badge
                      variant="outline"
                      className={cn(
                        "font-medium",
                        categoryColors[selectedEvent.category] ??
                          categoryColors["admin-settings"]
                      )}
                    >
                      {selectedEvent.category}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                    Action
                  </label>
                  <div className="mt-1 text-sm text-slate-900 dark:text-slate-100">
                    {selectedEvent.action}
                  </div>
                </div>
              </div>

              {/* Actor */}
              <div>
                <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                  Performed By
                </label>
                <div className="mt-1">
                  <div className="font-medium text-slate-900 dark:text-slate-100">
                    {selectedEvent.actor.displayName}
                  </div>
                  {selectedEvent.actor.email && (
                    <div className="text-sm text-slate-500 dark:text-slate-400">
                      {selectedEvent.actor.email}
                    </div>
                  )}
                </div>
              </div>

              {/* Target */}
              {selectedEvent.target && (
                <div>
                  <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                    Target User
                  </label>
                  <div className="mt-1">
                    <div className="font-medium text-slate-900 dark:text-slate-100">
                      {selectedEvent.target.displayName}
                    </div>
                    {selectedEvent.target.email && (
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {selectedEvent.target.email}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div>
                <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                  Summary
                </label>
                <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                  {selectedEvent.summary}
                </div>
              </div>

              {/* Reason */}
              {selectedEvent.details?.reason && (
                <div>
                  <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                    Reason
                  </label>
                  <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                    {selectedEvent.details.reason}
                  </div>
                </div>
              )}

              {/* Before/After */}
              {(selectedEvent.details?.before || selectedEvent.details?.after) && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedEvent.details?.before && (
                    <div>
                      <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                        Before
                      </label>
                      <pre className="mt-1 rounded-md bg-slate-100 dark:bg-gray-900 p-3 text-xs text-slate-700 dark:text-slate-300 overflow-x-auto">
                        {JSON.stringify(selectedEvent.details.before, null, 2)}
                      </pre>
                    </div>
                  )}
                  {selectedEvent.details?.after && (
                    <div>
                      <label className="text-xs font-medium uppercase text-slate-500 dark:text-slate-400">
                        After
                      </label>
                      <pre className="mt-1 rounded-md bg-slate-100 dark:bg-gray-900 p-3 text-xs text-slate-700 dark:text-slate-300 overflow-x-auto">
                        {JSON.stringify(selectedEvent.details.after, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

